import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import "card-pattern/dist/main.css";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useProfile } from "../../../contexts/ProfileContext";
import Loader from "../../../components/common/Loader";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import { TableColumn, TableRow } from "../../../components/common/Table/Table";
import { useNavigate , Link } from "react-router-dom";
import {
  fetchUBTList,
  fetchRegionOptions,
  fetchAreaOptions,
  fetchCenterOptions,
  fetchLocationOptions,
  fetchCostCenterOptions,
  resetDependentDropdowns,
  setSelectedRegionCode,
  setSelectedAreaCode,
  setHasRunInitialSearch,
  setSelectedCenterCode,
  setSearchParams,
  setSortByCostCenter,
  restoreSearchFilters,
  setSelectedEntityCode,
  setSelectedLocationCode,
  setSelectedCostCenterCode,
  setSelectedSearchBy,
  setSelectedSearchByText,
  setSelectedFromId,
  setSelectedToId,
  searchByOptions,
  defaultOption,
  defaultRegionOption,
  exportGLDataCSV,
  exportUBTDetailsByCriteriaCSV,
  downloadPrivilegeRolesCSV,
  selectPrivilegeRolesLoading,
  selectPrivilegeRolesError,
} from "../../../store/slices/selectUBTSlice";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import {
  TEAM_SUMMARY_OPTION_ID,
  TEAM_DETAILS_OPTION_ID,
  EXPORT_GL_DATA_OPTION_ID,
  EXPORT_UBT_DETAILS_OPTION_ID,
} from "../../../utils/reportConstants";
import {
  getFindAndSelectUBTOptions,
  EXPORT_MEMBERSHIP_COUNT_CSV_ID,
  EXPORT_PRIVILEGE_ROLES_OPTION_ID,
} from "../../../constants/reportDropdownOptions";
import {
  downloadDisplaySummaryAndDetailsReport,
  isPdfDownloadLoading,
  downloadMembershipCountCSV,
  selectMembershipCountLoading,
  selectMembershipCountError,
} from "../../../store/slices/teamsReportSlice";
import "form-pattern/dist/main.css";
import UBTSearchContainer from "./UBTSearchContainer";
import UBTSearchResults from "../../../components/common/UBTSearchResults";
import PopUp from "../../../components/common/PopUp/PopUp";
import SelectUBTHelpModal from "./SelectUBTHelpModal";

import {
  saveSearchToSession,
  loadSearchFromSession,
  type SharedSearchData,
} from "../../../utils/searchSessionStorage";
import {
  isAdminWithEditPrivileges,
  hasEditPrivilegeForCostCenter,
} from "../../../utils/editPrivilegeCheck";
import { teamStorage } from "../../../utils/teamStorage";
import { getCostCenterParam } from "../../../utils/costCenter";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import type {
  FindAndSelectUBTMode,
} from "../../../types/selectUBT.types";

/** Maps UI table column keys to the API sortBy values expected by fetchUBTList (select-ubt route only). */
const FIND_SELECT_SORT_COLUMN_MAP: Record<string, string> = {
  id: 'id',
  ubtName: 'name',
};

/** Resolves the sortBy value to send: legacy uppercase mapping for select-ubt, logical key unchanged for edit-ubt. */
const resolveSortBy = (mode: FindAndSelectUBTMode, key: string): string =>
  mode === "edit" ? key : (FIND_SELECT_SORT_COLUMN_MAP[key] ?? key.toUpperCase());

const columns: TableColumn[] = [
  { key: "id", label: "ID", isSortable: true },
  {
    key: "ubtName", isSortable: true ,
    label: "Name of UBT",
    className: "md:max-w-[12rem] whitespace-normal break-words",
  },
  { key: "teamRating", label: "Team Rating" },
  { key: "glstring", label: "GL String" },
  { key: "entity", label: "Entity" },
  { key: "area", label: "Area" },
  { key: "location", label: "Location" },
  { key: "costCenter", label: "Cost Center" },
  { key: "shift", label: "Shift", className: "md:pr-12!" },
  { key: "selectAction", label: "View Details" },
];

/** Edit-ubt-only columns: sends logical sortColumn keys per the /ubt/teams/search sorting contract. */
const editColumns: TableColumn[] = [
  { key: "id", label: "ID", isSortable: true, sortColumn: "id" },
  {
    key: "ubtName",
    label: "Name of UBT",
    isSortable: true,
    sortColumn: "name",
    className: "md:max-w-[12rem] whitespace-normal break-words",
  },
  { key: "teamRating", label: "Team Rating", isSortable: true, sortColumn: "teamRating" },
  { key: "glstring", label: "GL String" },
  { key: "entity", label: "Entity", isSortable: true, sortColumn: "entity" },
  { key: "area", label: "Area", isSortable: true, sortColumn: "area" },
  { key: "location", label: "Location", isSortable: true, sortColumn: "location" },
  { key: "costCenter", label: "Cost Center", isSortable: true, sortColumn: "costCenter" },
  { key: "shift", label: "Shift", isSortable: true, sortColumn: "shift", className: "md:pr-12!" },
  { key: "selectAction", label: "View Details" },
];

const viewDetailsOptions = [
  { id: 0, label: "Select", value: "Select" },
  { id: 2, label: "Projects", value: "projects" },
  { id: 3, label: "Members", value: "members" },
  { id: 4, label: "History", value: "history" },
  { id: 5, label: "P2P", value: "path-to-performance" },
  { id: 6, label: "Edit UBT", value: "edit" },
];

const viewDetailsOptionsWithoutEdit = viewDetailsOptions.filter(
  (opt) => opt.value !== "edit",
);

/** Maps a report-identifier string to its corresponding dropdown option ID. */
type ReportOptionId = string | number | undefined;
const REPORT_IDENTIFIER_ID_MAP: Record<string, ReportOptionId> = {
  teamsummary: TEAM_SUMMARY_OPTION_ID,
  teamdetails: TEAM_DETAILS_OPTION_ID,
  exportgldatacsv: EXPORT_GL_DATA_OPTION_ID,
  exportubtdetailscsv: EXPORT_UBT_DETAILS_OPTION_ID,
};

/** Transforms session-stored report params into the GL data export API shape. */
const buildGLDataApiParams = (
  params: Record<string, string>,
  sortColumn: string,
  sortDirection: string,
): Record<string, string> => {
  const apiParams: Record<string, string> = {};
  if (params.regionCd) apiParams.regionCode = params.regionCd;
  if (params.entityCd) apiParams.entityCode = params.entityCd;
  if (params.areaCd) apiParams.areaCode = params.areaCd;
  if (params.centerCd) apiParams.serviceAreaCode = params.centerCd;
  if (params.locationCd) apiParams.locationCode = params.locationCd;
  if (params.costCenterCd) apiParams.costCenter = params.costCenterCd;
  if (params.searchString) apiParams.searchString = params.searchString;
  if (params.queryBy) apiParams.searchBy = params.queryBy;
  // fromId/toId are only valid when searching by UBT ID#
  if (params.fromId) apiParams.fromId = params.fromId;
  if (params.toId) apiParams.toId = params.toId;
  apiParams.sortColumn =
    sortColumn === "id" ? "TEAMID" : sortColumn.toUpperCase();
  apiParams.sortOrder = sortDirection;
  return apiParams;
};

/** Transforms session-stored report params into the UBT Details by-criteria export API shape. */
const buildUBTDetailsApiParams = (
  params: Record<string, string>,
  rawCostCenter:
    | { isAllSelected: boolean; selectedIds: (string | number)[] }
    | undefined,
  sortColumn: string,
  sortDirection: string,
): Record<string, string> => {
  const apiParams: Record<string, string> = {};
  if (params.regionCd) apiParams.regionCode = params.regionCd;
  if (params.entityCd) apiParams.entityCode = params.entityCd;
  if (params.areaCd) apiParams.areaCode = params.areaCd;
  if (params.centerCd) apiParams.serviceAreaCode = params.centerCd;
  if (params.locationCd) apiParams.locationCode = params.locationCd;
  const ubtDetailsCostCenter = getCostCenterParam(rawCostCenter, "~");
  if (ubtDetailsCostCenter) apiParams.costCenter = ubtDetailsCostCenter;
  if (params.searchString) apiParams.searchString = params.searchString;
  if (params.queryBy) apiParams.searchBy = params.queryBy;
  // fromId/toId are only valid when searching by UBT ID#
  if (params.fromId) apiParams.fromId = params.fromId;
  if (params.toId) apiParams.toId = params.toId;
  apiParams.sortColumn =
    sortColumn === "id" ? "TEAMID" : sortColumn.toUpperCase();
  apiParams.sortOrder = sortDirection;
  return apiParams;
};

/** Shape of a UBT team detail row returned by the selectUBTList API. */
interface UBTTeamDetail {
  teamRating?: string;
  glstring?: string;
  entity?: string;
  area?: string;
  location?: string;
  costCenter?: string;
  shift?: string;
  kpGlRegionCd?: string;
  kpAreaCd?: string;
  kpSvcArea?: string;
  glLocCd?: string;
}

/** Shape of a UBT team record returned by the selectUBTList API. */
interface UBTTeamItem {
  id?: string | number;
  ubtName?: string;
  nuid?: string | null;
  isDelete?: boolean;
  details?: UBTTeamDetail[];
}

const FindAndSelectUBT: React.FC<{ mode?: FindAndSelectUBTMode }> = ({
  mode = "select",
}) => {
  const dispatch = useAppDispatch();
  const { loading: profileLoading } = useProfile();
  const profile = useAppSelector((state) => state.profile?.profile);
  const isAdmin = isAdminWithEditPrivileges(profile);
  const reportDownloadLoading = useAppSelector(isPdfDownloadLoading);
  const membershipCountLoading = useAppSelector(selectMembershipCountLoading);
  const membershipCountError = useAppSelector(selectMembershipCountError);
  const privilegeRolesLoading = useAppSelector(selectPrivilegeRolesLoading);
  const privilegeRolesError = useAppSelector(selectPrivilegeRolesError);

  const {
    selectUBTList = [],
    editUBTList = [],
    currentPage,
    perPage,
    totalItems,
    regionOptions,
    entityOptions,
    areaOptions,
    centerOptions,
    locationOptions,
    costCenterOptions,
    selectedRegionCode,
    selectedAreaCode,
    selectedCenterCode,
    selectedEntityCode,
    selectedLocationCode,
    selectedCostCenterCode,
    loadingDropdowns,
    loading,
    teamsCount,
    hasRunInitialSearch,
    searchParams,
    sortByCostCenter,
    selectedSearchBy,
    selectedSearchByText,
    selectedFromId,
    selectedToId,
    exportGLDataLoading,
    exportUBTDetailsLoading,
  } = useAppSelector((state) => state.selectUBTSlice);

  // Safe fallback values to prevent undefined access
  const safeSelectedRegionCode = selectedRegionCode ?? defaultRegionOption;
  const safeSelectedAreaCode = selectedAreaCode ?? defaultOption;
  const safeSelectedCenterCode = selectedCenterCode ?? defaultOption;
  const safeSelectedEntityCode = selectedEntityCode ?? defaultOption;
  const safeSelectedCostCenterCode = selectedCostCenterCode ?? {
    isAllSelected: true,
    selectedIds: [],
  };
  const safeSelectedLocationCode = selectedLocationCode ?? defaultOption;
  const safeSortByCostCenter = sortByCostCenter ?? "id";

  const safeSelectedSearchBy = selectedSearchBy ?? searchByOptions[0];
  const safeSelectedSearchByText = selectedSearchByText ?? "";
  const safeSelectedFromId = selectedFromId ?? "";
  const safeSelectedToId = selectedToId ?? "";

  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<string>("id");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingSessionData, setIsLoadingSessionData] = useState(false);
  // Computed synchronously to avoid the useState+useEffect timing gap where
  // loadingDropdowns becomes true but the loader condition hasn't updated yet.
  const isLoadingData = useMemo(
    () => profileLoading || loadingDropdowns || isLoadingSessionData,
    [profileLoading, loadingDropdowns, isLoadingSessionData],
  );
  const [sessionRestored, setSessionRestored] = useState(false);
  const [shouldScrollToResults, setShouldScrollToResults] = useState(false);
  const [showMembershipErrorBanner, setShowMembershipErrorBanner] =
    useState<boolean>(false);
  const [showPrivilegeRolesErrorBanner, setShowPrivilegeRolesErrorBanner] =
    useState<boolean>(false);
  const [exportWarningMessage, setExportWarningMessage] = useState<string>("");
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  // Open help modal when the popup's "additional page help" anchor is clicked
  useEffect(() => {
    const handleHelpClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-open-modal="select-ubt-help"]')) {
        e.preventDefault();
        setIsHelpModalOpen(true);
      }
    };
    document.addEventListener('click', handleHelpClick);
    return () => document.removeEventListener('click', handleHelpClick);
  }, []);

  // Refs to track latest values for effects that should only run on specific deps
  const hasRunInitialSearchRef = useRef(hasRunInitialSearch);
  const currentPageRef = useRef(currentPage);
  const perPageRef = useRef(perPage);
  const sortColumnRef = useRef(sortColumn);
  const sortDirectionRef = useRef(sortDirection);
  const searchParamsRef = useRef(searchParams);
  const resultsRef = useRef<HTMLDivElement>(null);
  // Ref for scrolling to the export warning banner
  const exportWarningRef = useRef<HTMLDivElement>(null);

  // Helper to fetch dependent API data based on session filters sequentially
  const fetchDependentData = useCallback(
    async (storedSearch: SharedSearchData) => {
      const regionCode = storedSearch.selectedRegionCode?.value;
      if (!regionCode || regionCode === "Select" || regionCode === "") return;

      const areaCode = storedSearch.selectedAreaCode?.value;
      const centerCode = storedSearch.selectedCenterCode?.value;
      const locationCode = storedSearch.selectedLocationCode?.value;
      const sortBy = storedSearch.sortByCostCenter ?? "id";

      try {
        await dispatch(
          fetchAreaOptions({ regionCode, sortBy, isRestored: true }),
        ).unwrap();

        // If area exists, fetch center options after area completes
        if (areaCode && areaCode !== defaultOption.value) {
          await dispatch(
            fetchCenterOptions({
              regionCode,
              areaCode,
              sortBy,
              isRestored: true,
            }),
          ).unwrap();

          // If center exists, fetch location options after center completes
          if (centerCode && centerCode !== defaultOption.value) {
            await dispatch(
              fetchLocationOptions({
                regionCode,
                areaCode,
                centerCode,
                sortBy,
                isRestored: true,
              }),
            ).unwrap();

            // If location exists, fetch cost center options after location completes
            if (locationCode && locationCode !== defaultOption.value) {
              await dispatch(
                fetchCostCenterOptions({
                  regionCode,
                  areaCode,
                  centerCode,
                  locationCode,
                  sortBy,
                  isRestored: true,
                }),
              ).unwrap();
            }
          }
        }
      } catch {
        // Silently handle errors fetching dependent dropdown data;
        // the user can retry by changing their selection.
      }
    },
    [dispatch],
  );

  // Keep refs updated
  useEffect(() => {
    hasRunInitialSearchRef.current = hasRunInitialSearch;
    currentPageRef.current = currentPage;
    perPageRef.current = perPage;
    sortColumnRef.current = sortColumn;
    sortDirectionRef.current = sortDirection;
    searchParamsRef.current = searchParams;
  }, [
    hasRunInitialSearch,
    currentPage,
    perPage,
    sortColumn,
    sortDirection,
    searchParams,
  ]);

  useEffect(() => {
    dispatch(fetchRegionOptions());

    // When session data exists the session-restoration effect restores all filter
    // values and re-runs the search. Applying profile defaults here would overwrite
    // persisted selections and hide the results table (via setHasRunInitialSearch).
    const hasSessionData = !!loadSearchFromSession();
    if (!hasSessionData) {
      dispatch(setSelectedRegionCode(defaultRegionOption));
      dispatch(resetDependentDropdowns({ level: "region" }));
      dispatch(setHasRunInitialSearch(false));
      dispatch(setSelectedSearchBy(searchByOptions[0]));
      dispatch(setSelectedSearchByText(""));
      dispatch(setSelectedFromId(""));
      dispatch(setSelectedToId(""));
    }
  }, [dispatch]);

  useEffect(() => {
    // On initial load, if there is no region in session, set region from profile slice
    if (profile && !loadSearchFromSession()?.selectedRegionCode) {
      const newRegion = {
        id: profile.primaryRegion ?? "",
        label: profile.regionDescription ?? "Select",
        value: profile.primaryRegion ?? "",
      };
      handleRegionChange(newRegion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, profile]);

  // Load search filters from sessionStorage and fetch dependent data sequentially
  useEffect(() => {
    if (sessionRestored || regionOptions.length === 0) return;

    const storedSearch = loadSearchFromSession();
    if (!storedSearch) {
      setSessionRestored(true);
      return;
    }

    const filtersToRestore: Record<string, unknown> = {};
    const filterKeys: Array<keyof typeof storedSearch> = [
      "selectedRegionCode",
      "selectedEntityCode",
      "selectedAreaCode",
      "selectedCenterCode",
      "selectedLocationCode",
      "selectedCostCenterCode",
      "sortByCostCenter",
      "selectedSearchBy",
      "selectedSearchByText",
      "selectedFromId",
      "selectedToId",
    ];

    filterKeys.forEach((key) => {
      if (storedSearch[key]) {
        filtersToRestore[key] = storedSearch[key];
      }
    });

    if (Object.keys(filtersToRestore).length > 0) {
      dispatch(restoreSearchFilters(filtersToRestore));
    }

    // Fetch all dependent API data sequentially
    const loadData = async () => {
      setIsLoadingSessionData(true);
      await fetchDependentData(storedSearch);
      setIsLoadingSessionData(false);
      setSessionRestored(true);
    };

    loadData();
  }, [dispatch, regionOptions.length, sessionRestored, fetchDependentData]);

  // Simplified initialization check - only depends on loading states and region selection
  useEffect(() => {
    if (
      !isInitialized &&
      !loading &&
      regionOptions.length > 0 &&
      entityOptions.length > 0 &&
      !isLoadingData &&
      !isLoadingSessionData &&
      safeSelectedRegionCode.value &&
      safeSelectedRegionCode.value !== "Select"
    ) {
      setIsInitialized(true);
    }
  }, [
    isInitialized,
    regionOptions.length,
    entityOptions.length,
    isLoadingData,
    isLoadingSessionData,
    safeSelectedRegionCode.value,
  ]);

  // Show error banner when membership count export fails; auto-dismiss after 5 s
  useEffect(() => {
    if (membershipCountError) {
      setShowMembershipErrorBanner(true);
      const timer = setTimeout(() => {
        setShowMembershipErrorBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [membershipCountError]);

  // Show error banner when privilege roles export fails; auto-dismiss after 5 s
  useEffect(() => {
    if (privilegeRolesError) {
      setShowPrivilegeRolesErrorBanner(true);
      const timer = setTimeout(() => {
        setShowPrivilegeRolesErrorBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [privilegeRolesError]);

  // Auto-dismiss export warning banner after 5 s and scroll to it
  useEffect(() => {
    if (!exportWarningMessage) return;
    exportWarningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timer = setTimeout(() => setExportWarningMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [exportWarningMessage]);

  // Scroll to results after data loads successfully
  useEffect(() => {
    if (
      !loading &&
      !isLoadingData &&
      hasRunInitialSearch &&
      shouldScrollToResults
    ) {
      if (
        resultsRef.current &&
        typeof resultsRef.current.scrollIntoView === "function"
      ) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setShouldScrollToResults(false);
    }
  }, [loading, isLoadingData, hasRunInitialSearch, shouldScrollToResults]);

  const handleRegionChange = useCallback(
    (regionOption: DropdownOption | null) => {
      const newRegion = regionOption ?? defaultRegionOption;
      dispatch(setSelectedRegionCode(newRegion));
      dispatch(resetDependentDropdowns({ level: "region" }));
      if (
        newRegion.value &&
        newRegion.value !== "Select" &&
        newRegion.value !== ""
      ) {
        dispatch(
          fetchAreaOptions({
            regionCode: newRegion.value,
          }),
        );
      }
    },
    [dispatch],
  );

  const handleAreaChange = useCallback(
    (areaOption: DropdownOption | null, regionCode: string) => {
      const newArea = areaOption ?? defaultOption;
      dispatch(setSelectedAreaCode(newArea));
      dispatch(resetDependentDropdowns({ level: "area" }));

      if (newArea.value && regionCode) {
        const areaCode = newArea.value ?? String(newArea.id);
        dispatch(
          fetchCenterOptions({
            regionCode,
            areaCode,
          }),
        );
      }
    },
    [dispatch],
  );

  const handleEntityChange = useCallback(
    (entityOption: DropdownOption | null) => {
      const newEntity = entityOption ?? defaultOption;
      dispatch(setSelectedEntityCode(newEntity));
    },
    [dispatch],
  );

  const handleCenterChange = useCallback(
    (
      centerOption: DropdownOption | null,
      regionCode: string,
      areaCode: string,
    ) => {
      const newCenter = centerOption ?? defaultOption;

      if (
        newCenter.value &&
        regionCode &&
        areaCode &&
        areaCode !== defaultOption.value
      ) {
        dispatch(setSelectedCenterCode(newCenter));
        dispatch(resetDependentDropdowns({ level: "center" }));
        const centerCode = newCenter.value ?? String(newCenter.id);
        dispatch(
          fetchLocationOptions({
            regionCode,
            areaCode,
            centerCode,
          }),
        );
      }
    },
    [dispatch],
  );

  const handleLocationChange = useCallback(
    (
      locationOption: DropdownOption | null,
      regionCode: string,
      areaCode: string,
      centerCode: string,
      sortBy: string,
    ) => {
      const newLocation = locationOption ?? defaultOption;
      if (
        newLocation.value &&
        regionCode &&
        areaCode &&
        centerCode !== defaultOption.value
      ) {
        dispatch(setSelectedLocationCode(newLocation));
        dispatch(resetDependentDropdowns({ level: "location" }));
        dispatch(setSortByCostCenter(sortBy));
        const locationCode = newLocation.value ?? String(newLocation.id);
        dispatch(
          fetchCostCenterOptions({
            regionCode,
            areaCode,
            centerCode,
            locationCode,
            sortBy,
          }),
        );
      }
    },
    [dispatch],
  );
  const onSearchByTextChange = useCallback(
    (searchByText: string) => {
      dispatch(setSelectedSearchByText(searchByText));
    },
    [dispatch],
  );

  const onSearchByChange = useCallback(
    (searchByOption: DropdownOption) => {
      dispatch(setSelectedSearchBy(searchByOption));
      // Clear the search text whenever the Search By type changes to avoid carrying
      // over a value that is invalid for the newly selected type (e.g., letters typed
      // for UBT Name are not valid wildcard patterns for UBT ID#, and vice-versa).
      dispatch(setSelectedSearchByText(""));
      // fromId/toId are only valid for UBT ID#; clear them when switching away.
      if (searchByOption?.value !== "UBTId") {
        dispatch(setSelectedFromId(""));
        dispatch(setSelectedToId(""));
      }
    },
    [dispatch],
  );

  const onFromIdChange = useCallback(
    (value: string) => {
      dispatch(setSelectedFromId(value));
    },
    [dispatch],
  );

  const onToIdChange = useCallback(
    (value: string) => {
      dispatch(setSelectedToId(value));
    },
    [dispatch],
  );

  const handleCostCenterChange = useCallback(
    (isAllSelected: boolean, selectedIds: (string | number)[]) => {
      dispatch(setSelectedCostCenterCode({ isAllSelected, selectedIds }));
    },
    [dispatch],
  );

  const handleCostCenterSortBy = useCallback(
    (
      locationCode: string,
      regionCode: string,
      areaCode: string,
      centerCode: string,
      sortBy: string,
    ) => {
      dispatch(setSortByCostCenter(sortBy));
      if (
        safeSelectedRegionCode.value &&
        safeSelectedRegionCode.value !== "Select"
      ) {
        dispatch(resetDependentDropdowns({ level: "location" }));
        dispatch(
          fetchCostCenterOptions({
            regionCode,
            areaCode,
            centerCode,
            locationCode,
            sortBy,
          }),
        );
      }
    },
    [dispatch, safeSelectedRegionCode.value],
  );

  const handleViewDetailsClick = useCallback(
    (selectedOption: DropdownOption, rowData: UBTTeamItem) => {
      const id = rowData?.id ?? "";
      if (id && selectedOption.value !== viewDetailsOptions[0].value) {
        teamStorage.setTeamId(id);
        if (selectedOption.value === "edit") {
          navigate("/home/add-profile", {
            state: {
              from: "Find and Select UBT",
              teamID: id,
              nuid: rowData?.nuid ?? null,
            },
          });
        } else if (selectedOption.value === "path-to-performance") {
          navigate("/home/path-to-performance", {
            state: {
              from: "Select UBT",
              teamID: id,
              nuid: rowData?.nuid ?? null,
            },
          });
        } else {
          navigate(`/home/${selectedOption.value}`, {
            state: {
              from: "Find and Select UBT",
              teamId: id,
              nuid: rowData?.nuid ?? null,
            },
          });
        }
      }
    },
    [navigate],
  );

  // Unified function to build params and fetch UBT list
  const fetchWithParams = useCallback(
    (
      overrides: Partial<{
        page: number;
        perPage: number;
        sortBy?: string;
        sortOrder: "ASC" | "DESC";
      }> = {},
    ) => {
      const param = {
        ...searchParams,
        page: overrides.page ?? currentPage,
        perPage: overrides.perPage ?? perPage,
        sortBy: overrides.sortBy ?? resolveSortBy(mode, sortColumn),
        sortOrder: overrides.sortOrder ?? sortDirection,
        resultMode: mode,
      };
      dispatch(fetchUBTList(param));
    },
    [dispatch, mode, currentPage, perPage, sortColumn, sortDirection, searchParams],
  );

  const handleSearch = useCallback(
    (newSearchParams: Record<string, unknown>) => {
      dispatch(setHasRunInitialSearch(true));
      dispatch(setSearchParams(newSearchParams));

      // Save current search filters to sessionStorage
      const searchData = {
        selectedRegionCode: safeSelectedRegionCode,
        selectedAreaCode: safeSelectedAreaCode,
        selectedCenterCode: safeSelectedCenterCode,
        sortByCostCenter: safeSortByCostCenter,
        selectedLocationCode: safeSelectedLocationCode,
        selectedCostCenterCode: safeSelectedCostCenterCode,
        selectedEntityCode: safeSelectedEntityCode,
        selectedSearchBy: safeSelectedSearchBy,
        selectedSearchByText: safeSelectedSearchByText,
        selectedFromId: safeSelectedFromId,
        selectedToId: safeSelectedToId,
      };
      saveSearchToSession(searchData);

      // Fetch with page 1 and new search params
      const param = {
        page: 1,
        perPage,
        sortBy: resolveSortBy(mode, sortColumn),
        sortOrder: sortDirection,
        ...newSearchParams,
        countOnly: true,
        resultMode: mode,
      };
      dispatch(fetchUBTList(param));
      setShouldScrollToResults(true);
    },
    [
      dispatch,
      mode,
      perPage,
      sortColumn,
      sortDirection,
      safeSelectedRegionCode,
      safeSelectedAreaCode,
      safeSelectedCenterCode,
      safeSelectedEntityCode,
      safeSelectedLocationCode,
      safeSelectedCostCenterCode,
      safeSortByCostCenter,
      safeSelectedSearchBy,
      safeSelectedSearchByText,
      safeSelectedFromId,
      safeSelectedToId,
    ],
  );

  // Run search only when search criteria is available in session storage
  useEffect(() => {
    const storedSearch = loadSearchFromSession();
    if (isInitialized && sessionRestored && storedSearch) {
      const newParam = {
        searchBy: storedSearch.selectedSearchBy?.value,
        searchText: storedSearch.selectedSearchByText,
        fromId:
          storedSearch.selectedSearchBy?.value === "UBTId"
            ? storedSearch.selectedFromId
            : undefined,
        toId:
          storedSearch.selectedSearchBy?.value === "UBTId"
            ? storedSearch.selectedToId
            : undefined,
        regionCode: storedSearch.selectedRegionCode?.value,
        entityCode: storedSearch.selectedEntityCode?.value,
        areaCode: storedSearch.selectedAreaCode?.value,
        centerCode: storedSearch.selectedCenterCode?.value,
        locationCode: storedSearch.selectedLocationCode?.value,
        costCenterIds: storedSearch.selectedCostCenterCode?.isAllSelected
          ? "All"
          : (storedSearch.selectedCostCenterCode?.selectedIds ?? []),
      };
      const param = {
        page: 1,
        perPage: 20,
        countOnly: true, // To fetch Teams count as well
        sortBy: resolveSortBy(mode, sortColumn),
        sortOrder: sortDirection,
        ...newParam,
        resultMode: mode,
      };
      dispatch(setHasRunInitialSearch(true));
      dispatch(setSearchParams(newParam));
      dispatch(fetchUBTList(param));
      setShouldScrollToResults(true);
    }
  }, [dispatch, isInitialized, sessionRestored]);

  const handleActionClick = useCallback(
    (rowData: UBTTeamItem) => {
      const id = rowData?.id ?? "";
      if (id) {
        teamStorage.setTeamId(id);
        navigate("/home/projects", {
          state: { teamId: id, nuid: rowData?.nuid ?? null },
        });
      }
    },
    [navigate],
  );

  const handleBreadcrumbPrevious = useCallback((): void => {
    navigate("/home");
  }, [navigate]);

  const handlePageChange = useCallback(
    (p?: number) => {
      if (typeof p === "number") {
        fetchWithParams({ page: p });
      }
    },
    [fetchWithParams],
  );

  const handleDropdownSelect = useCallback(
    (team: UBTTeamItem) => (selectedValue: DropdownOption) =>
      handleViewDetailsClick(selectedValue, team),
    [handleViewDetailsClick],
  );

  const rows: TableRow[] = useMemo(() => {
    return ((selectUBTList as UBTTeamItem[]) ?? []).flatMap(
      (team: UBTTeamItem) => {
        // Determine per-team whether "Edit UBT" should appear in the dropdown.
        // Show it only when the user is an admin AND at least one cost center
        // in this team matches the user's editDataPrivileges.
        const teamHasEditAccess =
          isAdmin &&
          Array.isArray(team.details) &&
          team.details.some((d: UBTTeamDetail) =>
            hasEditPrivilegeForCostCenter(profile.editDataPrivileges, {
              regionCode: d.kpGlRegionCd ?? "",
              areaCode: d.kpAreaCd ?? "",
              centerCode: d.kpSvcArea ?? "",
              locationCode: d.glLocCd ?? "",
            }),
          );
        const teamOptions = teamHasEditAccess
          ? viewDetailsOptions
          : viewDetailsOptionsWithoutEdit;

        if (!team.details || team.details.length === 0) {
          return [
            {
              rowKey: `team-${team.id}-0`,
              id: team.id,
              ubtName: (
                <span
                  className="whitespace-normal wrap-break-word"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {team.ubtName ?? ""}
                </span>
              ),
              teamRating: "",
              glstring: "",
              entity: "",
              area: "",
              location: "",
              costCenter: "",
              shift: "",
              selectAction: (
                <Dropdown
                  label=""
                  selectedValue={viewDetailsOptions[0]}
                  options={teamOptions}
                  onSelect={handleDropdownSelect(team)}
                  buttonClassName="py-0! min-w-36 px-4!"
                  itemClassName="pr-4!"
                  menuClassName="min-w-36"
                />
              ),
            },
          ];
        }
        return team.details.map((detail: UBTTeamDetail, index: number) => ({
          rowKey: `team-${team.id}-${index}`,
          id: index === 0 ? team.id : "",
          ubtName:
            index === 0 ? (
              <span
                className="whitespace-normal wrap-break-word"
                style={{ overflowWrap: "anywhere" }}
              >
                {team.ubtName ?? ""}
              </span>
            ) : (
              ""
            ),
          teamRating: detail.teamRating ?? "",
          glstring: detail.glstring ?? "",
          entity: detail.entity ?? "",
          area: detail.area ?? "",
          location: detail.location ?? "",
          costCenter: detail.costCenter ?? "",
          shift: detail.shift ?? "",
          selectAction:
            index === 0 ? (
              <Dropdown
                label=""
                selectedValue={viewDetailsOptions[0]}
                options={teamOptions}
                onSelect={handleDropdownSelect(team)}
                buttonClassName="py-0! min-w-36 px-4!"
                itemClassName="pr-4!"
                menuClassName="min-w-36"
              />
            ) : null,
        }));
      },
    );
  }, [selectUBTList, handleDropdownSelect, isAdmin, profile]);

  // Edit-ubt-only: renders the API's flat, already-sorted rows as-is (no grouping,
  // no re-sorting); repeated teamId rows are valid and must all remain visible.
  const editRows: TableRow[] = useMemo(() => {
   const items = editUBTList ?? []; 
    return items.map((item, index) => {
      const isSameTeamAsPrevious = items[index - 1]?.teamId === item.teamId;
      const teamHasEditAccess =
        isAdmin &&
        hasEditPrivilegeForCostCenter(profile.editDataPrivileges, {
          regionCode: item.kpGlRegionCd ?? "",
          areaCode: item.kpAreaCd ?? "",
          centerCode: item.kpSvcArea ?? "",
          locationCode: item.glLocCd ?? "",
        });
      const teamOptions = teamHasEditAccess
        ? viewDetailsOptions
        : viewDetailsOptionsWithoutEdit;
      const team: UBTTeamItem = { id: item.teamId, ubtName: item.teamName, nuid: null };

      return {
        rowKey: `edit-team-${item.teamId}-${index}`,
        id: isSameTeamAsPrevious ? "" : item.teamId,
        ubtName: isSameTeamAsPrevious ? (
          ""
        ) : (
          <span
            className="whitespace-normal wrap-break-word"
            style={{ overflowWrap: "anywhere" }}
          >
            {item.teamName ?? ""}
          </span>
        ),
        teamRating: item.teamRating ?? "",
        glstring: item.glString ?? "",
        entity: item.company ?? "",
        area: item.kpAreaDesc ?? "",
        location: item.glLocNm ?? "",
        costCenter: item.costCenter ?? "",
        shift: item.shift ?? "",
        selectAction: isSameTeamAsPrevious ? null : (
          <Dropdown
            label=""
            selectedValue={viewDetailsOptions[0]}
            options={teamOptions}
            onSelect={handleDropdownSelect(team)}
            buttonClassName="py-0! min-w-36 px-4!"
            itemClassName="pr-4!"
            menuClassName="min-w-36"
          />
        ),
      };
    });
  }, [editUBTList, handleDropdownSelect, isAdmin, profile]);

  const handleSort = useCallback(
    (columnKey: string, direction: "ASC" | "DESC") => {
      const apiSortColumn = resolveSortBy(mode, columnKey);
      setSortColumn(columnKey);
      setSortDirection(direction);
      // Include searchParams for consistency with other handlers
      const param = {
        ...searchParams,
        page: mode === "edit" ? 1 : currentPage,
        perPage,
        sortBy: apiSortColumn,
        sortOrder: direction,
        countOnly: true,
        resultMode: mode,
      };
      dispatch(fetchUBTList(param));
    },
    [dispatch, mode, currentPage, perPage, searchParams],
  );

  /**
   * Builds common report parameters from sessionStorage for both Team Summary and Team Details.
   * Only includes params that have actual values from session storage.
   */
  const buildDisplayReportParams = useCallback(
    (
      reportIdentifier:
        | "teamsummary"
        | "teamdetails"
        | "exportgldatacsv"
        | "exporttestdatacsv",
      sortDirection: string = "asc",
    ) => {
      const storedSearch = loadSearchFromSession();

      if (!storedSearch) {
        return null;
      }

      // Build params from session storage, only including non-empty values.
      const params: Record<string, string> = {
        reportIdentifier,
        pageId: "ubtReportIdListUBT",
        sortColumn: "teamid",
        sortOrder: sortDirection,
      };

      // Single-select fields: add only if value exists
      if (storedSearch.selectedRegionCode?.value) {
        params.regionCd = storedSearch.selectedRegionCode.value;
      }
      if (storedSearch.selectedEntityCode?.value) {
        params.entityCd = storedSearch.selectedEntityCode.value;
      }
      if (storedSearch.selectedAreaCode?.value) {
        params.areaCd = storedSearch.selectedAreaCode.value;
      }
      if (storedSearch.selectedCenterCode?.value) {
        params.centerCd = storedSearch.selectedCenterCode.value;
      }
      if (storedSearch.selectedLocationCode?.value) {
        params.locationCd = storedSearch.selectedLocationCode.value;
      }

      // Cost center: "All" when all selected, comma-separated code prefixes otherwise.
      // extractCostCenterId strips everything after the first hyphen (e.g. "5109-Admin" → "5109").
      const costCenterValue = getCostCenterParam(
        storedSearch.selectedCostCenterCode,
        ",",
      );
      if (costCenterValue) {
        params.costCenterCd = costCenterValue;
      }

      // Search text: add only if non-empty
      if (storedSearch.selectedSearchByText) {
        params.searchString = storedSearch.selectedSearchByText;
      }

      // Project name: add only if non-empty
      if (storedSearch.selectedProjectName) {
        params.searchProjectString = storedSearch.selectedProjectName;
      }

      // Query by: add only if value exists
      if (storedSearch.selectedSearchBy?.value) {
        params.queryBy = storedSearch.selectedSearchBy.value;
      }

      // fromId/toId: only valid for UBT ID# search
      if (storedSearch.selectedSearchBy?.value === "UBTId") {
        if (storedSearch.selectedFromId) {
          params.fromId = storedSearch.selectedFromId;
        }
        if (storedSearch.selectedToId) {
          params.toId = storedSearch.selectedToId;
        }
      }

      return params;
    },
    [sortDirection],
  );

  /**
   * Dispatches Team Summary or Team Details report.
   */
  const dispatchDisplayReport = useCallback(
    async (
      reportId: string | number,
      reportIdentifier:
        | "teamsummary"
        | "teamdetails"
        | "exportgldatacsv"
        | "exportubtdetailscsv",
    ) => {
      const expectedId = REPORT_IDENTIFIER_ID_MAP[reportIdentifier];
      if (reportId !== expectedId) return false;

      const params = buildDisplayReportParams(
        reportIdentifier === "exportubtdetailscsv"
          ? "exportgldatacsv"
          : reportIdentifier,
        sortDirection,
      );
      if (!params) return true;

      // Report/export sort contracts are independent of the table's logical
      // sort keys; edit-ubt must not leak keys like "area" into these params.
      const exportSortColumn = mode === "edit" ? "id" : sortColumn;

      if (reportIdentifier === "exportgldatacsv") {
        await dispatch(
          exportGLDataCSV(
            buildGLDataApiParams(params, exportSortColumn, sortDirection),
          ),
        );
      } else if (reportIdentifier === "exportubtdetailscsv") {
        const rawCostCenter = loadSearchFromSession()?.selectedCostCenterCode;
        await dispatch(
          exportUBTDetailsByCriteriaCSV(
            buildUBTDetailsApiParams(
              params,
              rawCostCenter,
              exportSortColumn,
              sortDirection,
            ),
          ),
        );
      } else {
        dispatch(downloadDisplaySummaryAndDetailsReport(params));
      }
      return true;
    },
    [dispatch, mode, buildDisplayReportParams, sortColumn, sortDirection],
  );

  /**
   * Handles report export (PDF or CSV) based on selected report type.
   * Passes search filter params to the API for filtered reports.
   */
  const handleMembershipCountExport = useCallback(async () => {
    const storedSearch = loadSearchFromSession();
    await dispatch(
      downloadMembershipCountCSV({
        sortBy: (mode === "edit" ? undefined : sortColumn) || undefined,
        sortOrder: sortDirection,
        regionCode: storedSearch?.selectedRegionCode?.value ?? undefined,
        entityCode: storedSearch?.selectedEntityCode?.value ?? undefined,
        areaCode: storedSearch?.selectedAreaCode?.value ?? undefined,
        serviceAreaCode: storedSearch?.selectedCenterCode?.value ?? undefined,
        locationCode: storedSearch?.selectedLocationCode?.value ?? undefined,
        costCenter:
          getCostCenterParam(storedSearch?.selectedCostCenterCode, ",") || undefined,
        searchString: storedSearch?.selectedSearchByText ?? undefined,
        nuid: profile?.nuid ? String(profile.nuid) : null,
      }),
    );
  }, [dispatch, mode, sortColumn, sortDirection, profile?.nuid]);

  const handlePrivilegeRolesExport = useCallback(async () => {
    const storedSearch = loadSearchFromSession();
    const isUbtIdSearch = storedSearch?.selectedSearchBy?.value === "UBTId";
    let queryBy = "deptname";
    if (isUbtIdSearch) {
      queryBy = "UBTId";
    } else if (storedSearch?.selectedSearchBy?.value === "UBTName") {
      queryBy = "teamname";
    }
    const result = await dispatch(
      downloadPrivilegeRolesCSV({
        sortColumn: "TEAMID",
        sortOrder: sortDirection,
        regionCode: storedSearch?.selectedRegionCode?.value?.toString(),
        companyCd: storedSearch?.selectedEntityCode?.value?.toString(),
        areaCode: storedSearch?.selectedAreaCode?.value?.toString(),
        serviceAreaCode: storedSearch?.selectedCenterCode?.value?.toString(),
        locationCode: storedSearch?.selectedLocationCode?.value?.toString(),
        deptIds:
          getCostCenterParam(storedSearch?.selectedCostCenterCode, ",") || "All",
        queryBy,
        searchString: storedSearch?.selectedSearchByText ?? undefined,
        fromId: isUbtIdSearch ? (storedSearch?.selectedFromId ?? undefined) : undefined,
        toId: isUbtIdSearch ? (storedSearch?.selectedToId ?? undefined) : undefined,
        nuid: profile?.nuid ? String(profile.nuid) : null,
      }),
    );
    if (result?.type === "selectUBT/downloadPrivilegeRolesCSV/rejected" && result?.payload === "RATE_LIMITED_429") {
      setExportWarningMessage(
        "We're currently processing multiple requests (4 in queue). Please try downloading the report again in a few moments",
      );
    }
  }, [dispatch, sortDirection, profile?.nuid]);

  const handleReportExport = useCallback(
    async (selectedReport: DropdownOption) => {
      if (selectedReport.id === EXPORT_MEMBERSHIP_COUNT_CSV_ID) {
        await handleMembershipCountExport();
        return;
      }

      if (selectedReport.id === EXPORT_PRIVILEGE_ROLES_OPTION_ID) {
        await handlePrivilegeRolesExport();
        return;
      }
      
      if (
        selectedReport.id === TEAM_SUMMARY_OPTION_ID &&
        (teamsCount ?? 0) > 200
      ) {
        setExportWarningMessage(
          "Team Summary Report is not Available for more than Max Team Size 200",
        );
        return;
      }
      if (
        selectedReport.id === TEAM_DETAILS_OPTION_ID &&
        (teamsCount ?? 0) > 200
      ) {
        setExportWarningMessage(
          "Team Detail Report is not Available for more than Max Team Size 200",
        );
        return;
      }
      setExportWarningMessage("");

      if (await dispatchDisplayReport(selectedReport.id, "teamsummary")) return;
      if (await dispatchDisplayReport(selectedReport.id, "teamdetails")) return;
      if (await dispatchDisplayReport(selectedReport.id, "exportgldatacsv"))
        return;
      if (await dispatchDisplayReport(selectedReport.id, "exportubtdetailscsv"))
        return;
    },
    [
      dispatch,
      dispatchDisplayReport,
      handleMembershipCountExport,
      handlePrivilegeRolesExport,
      teamsCount,
    ],
  );

  if (profileLoading) return <Loader />;
  if (exportGLDataLoading) return <Loader />;
  if (exportUBTDetailsLoading) return <Loader />;

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="flex justify-between items-end ">
        <div>
          <BreadCrumbs
            previousPage="UBT Home"
            onPreviousClick={handleBreadcrumbPrevious}
          />
        </div>

        <div className="mobile-margin lg:mt-15!">
          <Link
            to="/home/my-teams"
            data-analytics-type="link"
            state={{ from: "Find and Select UBT" }}
            className="no-underline! p1 -medium"
          >
            My Teams
          </Link>
          <Link
            to="/home/search-teams"
            data-analytics-type="link"
            state={{ from: "Find and Select UBT" }}
            className="no-underline! p1 -medium pl-6! "
          >
            Search UBTs Projects
          </Link>
          <Link
            to="/home/teams-reporting"
            data-analytics-type="link"
            state={{ from: "Find and Select UBT" }}
            className="no-underline! p1 -medium pl-6!"
          >
            Teams Reporting
          </Link>
        </div>
      </div>

      <div className="flex justify-between items-end mb-18! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
        <div className="">
          <h1 className="h1 tablet:mt-8! mobile:mt-3.5! mb-0!">
            Find and Select unit based teams
          </h1>
        </div>
      </div>

      <div className="bg-white border-solid border-2 border-border-light w-4xl rounded-xl p-8! mt-8! mb-24!">
        <div className="flex gap-x-1 mb-8!">
          <h3 className="h3 m-0!">Search unit based teams</h3>
          <PopUp
            className="ml-1! -medium"
            headerText="Select a UBT Page Help"
            message={`View the <a href="#" data-open-modal="select-ubt-help" rel="noopener noreferrer"> additional page help. </a><br/><br/>
              View the <a target="_blank" rel="noopener noreferrer" href="https://www.lmpartnership.org/path-to-performance/ubt-basics">the National LMP web site. </a>`}
          />
        </div>

        <UBTSearchContainer
          regionOptions={regionOptions}
          entityOptions={entityOptions}
          areaOptions={areaOptions}
          centerOptions={centerOptions}
          locationOptions={locationOptions}
          costCenterOptions={costCenterOptions}
          onRegionChange={handleRegionChange}
          onAreaChange={handleAreaChange}
          onEntityChange={handleEntityChange}
          onCenterChange={handleCenterChange}
          onSearch={handleSearch}
          onLocationChange={handleLocationChange}
          onCostCenterChange={handleCostCenterChange}
          onCostCenterSortBy={handleCostCenterSortBy}
          onSearchByTextChange={onSearchByTextChange}
          onSearchByChange={onSearchByChange}
          onFromIdChange={onFromIdChange}
          onToIdChange={onToIdChange}
          selectedSearchBy={safeSelectedSearchBy}
          selectedSearchByText={safeSelectedSearchByText}
          selectedFromId={safeSelectedFromId}
          selectedToId={safeSelectedToId}
          selectedRegionCode={safeSelectedRegionCode}
          selectedAreaCode={safeSelectedAreaCode}
          selectedCenterCode={safeSelectedCenterCode}
          sortByCostCenter={safeSortByCostCenter}
          selectedEntityCode={safeSelectedEntityCode}
          selectedLocationCode={safeSelectedLocationCode}
          selectedCostCenterCode={safeSelectedCostCenterCode}
          isLoadingData={isLoadingData}
        />
      </div>
      {exportWarningMessage && (
        <div ref={exportWarningRef}>
          <CommonErrorBanner
            variant="Alert"
            heading=""
            messageInfo={exportWarningMessage}
          />
        </div>
      )}
      {!loading &&
        !isLoadingData &&
        !reportDownloadLoading &&
        hasRunInitialSearch && (
          <div ref={resultsRef}>
            <UBTSearchResults
              rows={mode === "edit" ? editRows : rows}
              columns={mode === "edit" ? editColumns : columns}
              totalItems={totalItems}
              itemsPerPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onSort={handleSort}
              onActionClick={handleActionClick}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              teamsCount={teamsCount}
              reportOptions={getFindAndSelectUBTOptions()}
              onReportSelect={handleReportExport}
            />
          </div>
        )}
      {isLoadingData ||
      loading ||
      reportDownloadLoading ||
      membershipCountLoading ||
      privilegeRolesLoading ? (
        <Loader />
      ) : null}

      {showMembershipErrorBanner && membershipCountError && (
        <div
          role="alert"
          aria-live="assertive"
          className="ds-banner ds-banner__bg--white ds-banner--icon mb-6! mt-3!"
          style={{
            border: "1px solid var(--Color-Red, #C40000)",
            padding: "1px",
          }}
        >
          <div className="ds-banner__icon-img">
            <img
              src="/assets/images/alertSolid.svg"
              alt=""
              style={{ width: "38px", height: "38px" }}
            />
          </div>
          <div className="ds-banner__container">
            <div className="ds-banner__main flex flex-col">
              <div
                className="ds-banner__main-body p1 -medium"
                style={{
                  color: "var(--Color-Inky, #0D1C3D)",
                  marginTop: "7px",
                }}
              >
                {membershipCountError}
              </div>
            </div>
          </div>
        </div>
      )}
      {showPrivilegeRolesErrorBanner && privilegeRolesError && (
        <div
          role="alert"
          aria-live="assertive"
          className="ds-banner ds-banner__bg--white ds-banner--icon mb-6! mt-3!"
          style={{
            border: "1px solid var(--Color-Red, #C40000)",
            padding: "1px",
          }}
        >
          <div className="ds-banner__icon-img">
            <img
              src="/assets/images/alertSolid.svg"
              alt=""
              style={{ width: "38px", height: "38px" }}
            />
          </div>
          <div className="ds-banner__container">
            <div className="ds-banner__main flex flex-col">
              <div
                className="ds-banner__main-body p1 -medium"
                style={{
                  color: "var(--Color-Inky, #0D1C3D)",
                  marginTop: "7px",
                }}
              >
                {privilegeRolesError}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mb-10!">
        <div className="-mb-27! tablet:hidden">
          <ScrollToTopBtn />
        </div>
      </div>

      <SelectUBTHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
};

export default FindAndSelectUBT;
